import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import { UserNotificationSettings } from "@axl-dev/school-sdk";
import { Button, Checkbox, notification, Spin } from "antd";
import { useCallback, useMemo, useState } from "react";

import { useLoc, useSdk } from "@app/shared/lib/hooks";

type Props = {
  settings: UserNotificationSettings;
  onChange: (settings: UserNotificationSettings) => void;
};

export const NotificationSettingsForm: React.FC<Props> = ({
  settings,
  onChange,
}) => {
  const [isSaving, setSaving] = useState(false);
  const sdk = useSdk();
  const loc = useLoc();

  const [initialSettings, setInitialSettings] =
    useState<UserNotificationSettings>(settings);

  const [actualSettings, setActualSettings] =
    useState<UserNotificationSettings>(settings);
  const updateActualSettings = useCallback(
    (updates: Partial<UserNotificationSettings>) => {
      setActualSettings(
        (prev) => new UserNotificationSettings({ ...prev, ...updates }),
      );
    },
    [],
  );
  const hasChanges = useMemo(() => {
    if (!initialSettings || !actualSettings) return false;

    return (
      initialSettings.useEmail !== actualSettings.useEmail ||
      initialSettings.useTg !== actualSettings.useTg
    );
  }, [initialSettings, actualSettings]);

  const reset = useCallback(
    () => setActualSettings(new UserNotificationSettings(initialSettings)),
    [initialSettings],
  );

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const result = await sdk.api.notification.settings.save(actualSettings);
      if (result.success) {
        setInitialSettings(new UserNotificationSettings(actualSettings));
        notification.success({
          message: loc.word("Notif.settingsSaveSuccessMsg", {
            default: "Notification settings has been successfully saved",
          }),
          duration: 2,
        });
        onChange(new UserNotificationSettings(actualSettings));
        setSaving(false);

        return;
      }
    } finally {
      setSaving(false);
    }

    notification.error({ message: loc.word("Error occured"), duration: 2 });
  }, [sdk.api.notification.settings, actualSettings, loc, onChange]);

  return (
    <Spin tip={loc.word("Saving")} spinning={isSaving}>
      <div className="mb-20">
        <Checkbox
          checked={actualSettings.useEmail}
          onChange={(e) => updateActualSettings({ useEmail: e.target.checked })}
        >
          {loc.word("Notif.useEmailLabel", {
            default: "Use email to receive notifications",
          })}
        </Checkbox>
      </div>

      <div className="mb-20">
        <Checkbox
          checked={actualSettings.useTg}
          onChange={(e) => updateActualSettings({ useTg: e.target.checked })}
        >
          {loc.word("Notif.useTgLabel", {
            default: "Use Telegram to receive notifications",
          })}
        </Checkbox>

        {actualSettings.useTg && (
          <div className="ml-22 mt-10">
            {actualSettings.tgId ? (
              <div>
                <CheckCircleOutlined className="color-success" />
                &nbsp;
                {loc.word("Notif.tgSubscribedSuccessLabel", {
                  default:
                    "You have successfully connected Telegram for notifications",
                })}
                <div>
                  <a
                    href={actualSettings.tgLink}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <LinkOutlined />
                    {loc.word("Notif.tgLinkLabel", {
                      default: "Telegram bot link",
                    })}
                  </a>
                </div>
              </div>
            ) : (
              <div>
                <InfoCircleOutlined className="color-primary" />
                &nbsp;
                {loc.word("Notif.tgSubscribeHelp", {
                  default: "Use the link to subscribe for notifications",
                })}
                <div>
                  <a
                    href={actualSettings.tgLink}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <LinkOutlined />
                    {loc.word("Notif.tgLinkLabel", {
                      default: "Telegram bot link",
                    })}
                  </a>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {hasChanges && (
        <div>
          <Button
            onClick={() => reset()}
            type="default"
            icon={<CloseCircleOutlined />}
            className="mr-10"
          >
            {loc.word("Reset changes")}
          </Button>

          <Button
            type="primary"
            onClick={() => save()}
            icon={<CheckCircleOutlined />}
          >
            {loc.word("Save")}
          </Button>
        </div>
      )}
    </Spin>
  );
};
